import { useEffect, useState } from 'react';
import { Palette } from 'lucide-react';
import { Card, CardHeader, CardBody } from '@/components/ui/Card';
import { Spinner } from '@/components/ui/EmptyState';
import { useToast } from '@/components/ui/ToastContext';
import { supabase } from '@/lib/supabaseClient';
import { rememberSiteDesign, type SiteDesignMode } from '@/hooks/useSiteDesign';

// Picks which storefront design is live. Stored as 'site.design' in
// rxk_site_content (locale 'en'); read by useSiteDesign().
const OPTIONS: { value: SiteDesignMode; title: string; text: string }[] = [
  { value: 'classic', title: 'Classic', text: 'Everyone sees the current design.' },
  { value: 'preview', title: 'Preview', text: 'Staff who are signed in see the new design, with a small "preview" label. Customers still see the classic design.' },
  { value: 'new', title: 'New design', text: 'Everyone sees the new design.' },
];

export default function DesignPanel() {
  const { push } = useToast();
  const [mode, setMode] = useState<SiteDesignMode | null>(null);
  const [saving, setSaving] = useState<SiteDesignMode | null>(null);

  useEffect(() => {
    supabase
      .from('rxk_site_content')
      .select('value')
      .eq('key', 'site.design')
      .eq('locale', 'en')
      .maybeSingle()
      .then(({ data }) => {
        const v = (data as { value: string } | null)?.value;
        setMode(v === 'preview' || v === 'new' ? v : 'classic');
      });
  }, []);

  const choose = async (value: SiteDesignMode) => {
    if (value === mode || saving) return;
    if (value === 'new' && !window.confirm('Switch every visitor to the new design?')) return;
    setSaving(value);
    const {
      data: { user },
    } = await supabase.auth.getUser();
    const { error } = await supabase
      .from('rxk_site_content')
      .upsert({ key: 'site.design', locale: 'en', value, updated_by: user?.id ?? null }, { onConflict: 'key,locale' });
    setSaving(null);
    if (error) {
      push('error', error.message);
      return;
    }
    setMode(value);
    rememberSiteDesign(value);
    push('success', 'Saved. Reload the storefront to see it.');
  };

  return (
    <Card>
      <CardHeader title="Site design" subtitle="Switch between the classic and the new storefront design" icon={<Palette className="w-4 h-4" />} />
      <CardBody>
        {mode === null ? (
          <Spinner />
        ) : (
          <div className="grid gap-2.5 sm:grid-cols-3">
            {OPTIONS.map((o) => {
              const active = o.value === mode;
              return (
                <button
                  key={o.value}
                  type="button"
                  onClick={() => choose(o.value)}
                  disabled={!!saving}
                  className={`text-left rounded-xl border p-4 transition-colors cursor-pointer disabled:cursor-wait ${
                    active ? 'border-primary-700 bg-primary-50 ring-1 ring-primary-700' : 'border-border-subtle hover:border-ink-faint'
                  }`}
                >
                  <span className="flex items-center justify-between gap-2">
                    <span className="text-[14px] font-bold text-ink">{o.title}</span>
                    {active && <span className="text-[11px] font-semibold text-primary-800">Live</span>}
                    {saving === o.value && <span className="text-[11px] text-ink-faint">Saving…</span>}
                  </span>
                  <span className="block mt-1.5 text-[12.5px] leading-relaxed text-ink-faint">{o.text}</span>
                </button>
              );
            })}
          </div>
        )}
      </CardBody>
    </Card>
  );
}
